import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { type SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useGetAdminBusinessUnitsQuery } from 'services/api/adminBusinessUnits.api';
import {
  useCreateConversationEvalMutation,
  useGetEvalFromMessageMutation,
  useUpdateConversationEvalMutation,
  useUploadFileMutation,
} from 'services/api/conversationEvals.api';
import type { ConversationEval, MessageToEval } from 'services/types/conversationEvals';

interface ConversationEvalFormProps {
  conversationEval?: ConversationEval;
  onClose: () => void;
  messageId?: number;
}

const getRandomKey = () => Math.random().toString(36).substring(2, 15);

const getFileExtension = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  return extension;
};

const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];
  return imageExtensions.includes(getFileExtension(fileName));
};

const isDocumentFile = (fileName: string): boolean => {
  const documentExtensions = ['pdf', 'doc', 'docx', 'txt', 'rtf'];
  return documentExtensions.includes(getFileExtension(fileName));
};

const ConversationEvalForm: React.FC<ConversationEvalFormProps> = ({
  conversationEval = undefined,
  onClose,
  messageId = undefined,
}) => {
  const isEditMode = !!conversationEval;
  const [mockClientJSON, setMockClientJSON] = useState<string>(
    conversationEval?.mockClient ? JSON.stringify(conversationEval.mockClient, null, 2) : '{}'
  );
  const [toolCallsJSON, setToolCallsJSON] = useState<(string | null)[]>(
    conversationEval?.messages.map((m) => (m.toolCalls ? JSON.stringify(m.toolCalls, null, 2) : null)) || []
  );
  const [formData, setFormData] = useState<Omit<ConversationEval, 'id' | 'createdAt' | 'updatedAt'>>({
    businessUnitIds: conversationEval?.businessUnitIds || [],
    mockClient: conversationEval?.mockClient || {},
    messages: conversationEval?.messages.map((m) => ({ ...m, key: getRandomKey() })) || [
      {
        role: 'user',
        text: '',
        key: getRandomKey(),
      },
    ],
  });
  const [errors, setErrors] = useState<{
    businessUnitIds?: string;
    mockClient?: string;
    messages?: string;
    toolCalls: (string | null)[];
  }>({
    toolCalls: formData.messages.map(() => null),
  });
  const [evalFromMessage, setEvalFromMessage] = useState<ConversationEval | null>(null);
  const [createConversationEval] = useCreateConversationEvalMutation();
  const [updateConversationEval] = useUpdateConversationEvalMutation();
  const [uploadFile] = useUploadFileMutation();
  const { data: businessUnits } = useGetAdminBusinessUnitsQuery(null);
  const [getEvalFromMessage] = useGetEvalFromMessageMutation();
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: number]: boolean }>({});

  const handleBusinessUnitChange = (event: SelectChangeEvent<number[]>) => {
    setFormData({
      ...formData,
      businessUnitIds: event.target.value as number[],
    });
    setErrors({
      ...errors,
      businessUnitIds: undefined,
    });
  };

  const handleMessageChange = (index: number, field: keyof MessageToEval, value: string) => {
    const updatedMessages = [...formData.messages];
    if (field === 'text') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        text: value,
      };
    } else if (field === 'role') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        role: value as MessageToEval['role'],
      };
    } else if (field === 'fileUrl') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        fileUrl: value,
      };
    } else if (field === 'fileName') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        fileName: value,
      };
    } else if (field === 'mediaId') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        mediaId: value,
      };
    } else if (field === 'toolCalls') {
      const updatedToolCalls = [...toolCallsJSON];
      updatedToolCalls[index] = value;
      setToolCallsJSON(updatedToolCalls);
      setErrors({
        ...errors,
        toolCalls: errors.toolCalls.map((error, i) => (i === index ? null : error)),
      });
    } else if (field === 'toolCallId') {
      updatedMessages[index] = {
        ...updatedMessages[index],
        toolCallId: value,
      };
    }
    setFormData({
      ...formData,
      messages: updatedMessages,
    });
    setErrors({
      ...errors,
      messages: undefined,
    });
  };

  const handleFileUpload = async (index: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/rtf',
    ];

    if (!allowedTypes.includes(file.type)) {
      alert(
        'Tipo de archivo no permitido. Solo se permiten imágenes (jpg, png, gif, webp) y documentos (pdf, doc, docx, txt, rtf)'
      );
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo es demasiado grande. Máximo 10MB permitido');
      return;
    }

    setUploadingFiles((prev) => ({ ...prev, [index]: true }));

    try {
      const result = await uploadFile(file).unwrap();

      const updatedMessages = [...formData.messages];
      updatedMessages[index] = {
        ...updatedMessages[index],
        fileUrl: result.fileUrl,
        fileName: result.fileName,
        mediaId: result.mediaId,
      };

      setFormData({
        ...formData,
        messages: updatedMessages,
      });
    } catch (_error) {
      alert('Error al subir el archivo. Por favor, inténtalo de nuevo.');
    } finally {
      setUploadingFiles((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleRemoveFile = (index: number) => {
    const updatedMessages = [...formData.messages];
    updatedMessages[index] = {
      ...updatedMessages[index],
      fileUrl: null,
      fileName: null,
      mediaId: null,
    };

    setFormData({
      ...formData,
      messages: updatedMessages,
    });
  };

  const renderFilePreview = (message: MessageToEval, index: number) => {
    const { fileUrl, fileName, mediaId } = message;

    if (!fileUrl && !fileName && !mediaId) {
      return null;
    }

    const displayFileName = fileName || (fileUrl ? fileUrl.split('/').pop() : 'Archivo adjunto');

    return (
      <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(0, 0, 0, 0.05)', borderRadius: 1, border: '1px solid #ddd' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            📎 Archivo adjunto: {displayFileName}
          </Typography>
          <IconButton size="small" onClick={() => handleRemoveFile(index)} color="error">
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
        {fileName && isImageFile(fileName) && fileUrl && (
          <img
            src={fileUrl}
            alt={displayFileName}
            style={{
              maxWidth: '150px',
              maxHeight: '100px',
              borderRadius: '4px',
              border: '1px solid #ccc',
            }}
          />
        )}
        {fileName && isDocumentFile(fileName) && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            📄 {displayFileName}
          </Typography>
        )}
        {mediaId && (
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              display: 'block',
            }}
          >
            Media ID: {mediaId}
          </Typography>
        )}
      </Box>
    );
  };

  const handleExpectedChange = (
    index: number,
    field: 'responseDescription' | 'responseDistance',
    value: string | number
  ) => {
    const updatedMessages = [...formData.messages];
    const currentMessage = updatedMessages[index];
    updatedMessages[index] = {
      ...currentMessage,
      expected: {
        ...(currentMessage.expected || { responseDescription: '' }),
        [field]: field === 'responseDistance' ? Number(value) : value,
      },
    };
    setFormData({
      ...formData,
      messages: updatedMessages,
    });

    if (field === 'responseDescription') {
      const hasValidExpected = updatedMessages.some(
        (message) => message.expected?.responseDescription && message.expected.responseDescription.trim() !== ''
      );
      if (
        hasValidExpected &&
        errors.messages === 'At least one message must have a valid expected response description'
      ) {
        setErrors({
          ...errors,
          messages: undefined,
        });
      }
    }
  };

  const addMessage = () => {
    setFormData({
      ...formData,
      messages: [
        ...formData.messages,
        {
          role: 'user',
          text: '',
          toolCalls: null,
          key: getRandomKey(),
        },
      ],
    });
    setToolCallsJSON([...toolCallsJSON, null]);
    setErrors({
      ...errors,
      toolCalls: [...errors.toolCalls, null],
    });
  };

  const removeMessage = (index: number) => {
    const updatedMessages = [...formData.messages];
    updatedMessages.splice(index, 1);
    setFormData({
      ...formData,
      messages: updatedMessages,
    });
    setToolCallsJSON(toolCallsJSON.filter((_, i) => i !== index));
    setErrors({
      ...errors,
      toolCalls: errors.toolCalls.filter((_, i) => i !== index),
    });
  };

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {
      toolCalls: toolCallsJSON.map(() => null),
    };
    if (formData.businessUnitIds.length === 0) {
      newErrors.businessUnitIds = 'At least one business unit is required';
    }
    if (formData.messages.length === 0) {
      newErrors.messages = 'At least one message is required';
    } else {
      const invalidMessages = formData.messages.some(
        (msg) =>
          (msg.role !== 'assistant' && !msg.text && !msg.fileUrl && !msg.fileName && !msg.mediaId) ||
          (msg.role === 'assistant' && !msg.text && !msg.toolCalls && !msg.fileUrl && !msg.fileName && !msg.mediaId) ||
          (msg.role === 'tool' && !msg.toolCallId)
      );
      if (invalidMessages) {
        newErrors.messages =
          'All messages must have text, toolCalls, or file attachment. All tool messages must have a toolCallId';
      }
    }

    const hasValidExpected = formData.messages.some(
      (message) => message.expected?.responseDescription && message.expected.responseDescription.trim() !== ''
    );
    if (!hasValidExpected) {
      newErrors.messages = 'At least one message must have a valid expected response description';
    }

    toolCallsJSON.forEach((toolCall, index) => {
      if (toolCall) {
        try {
          JSON.parse(toolCall);
        } catch (_error) {
          newErrors.toolCalls[index] = 'Invalid JSON format for tool calls';
        }
      }
    });
    try {
      JSON.parse(mockClientJSON);
    } catch (_error) {
      newErrors.mockClient = 'Invalid JSON format';
    }
    setErrors(newErrors);

    return newErrors.toolCalls.every((error) => error === null) && Object.values(newErrors).length === 1;
  };

  const isFormValid = (): boolean => {
    if (formData.businessUnitIds.length === 0) return false;

    if (formData.messages.length === 0) return false;

    const hasValidExpected = formData.messages.some(
      (message) => message.expected?.responseDescription && message.expected.responseDescription.trim() !== ''
    );
    if (!hasValidExpected) return false;

    const hasToolCallErrors = toolCallsJSON.some((toolCall) => {
      if (toolCall) {
        try {
          JSON.parse(toolCall);
          return false;
        } catch {
          return true;
        }
      }
      return false;
    });
    if (hasToolCallErrors) return false;

    try {
      JSON.parse(mockClientJSON);
    } catch {
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }
    // Built locally rather than read back from state: setFormData is async, so submitting `formData`
    // right after setting it would drop the tool calls and mock client typed into the JSON editors.
    const payload = {
      ...formData,
      mockClient: JSON.parse(mockClientJSON) as typeof formData.mockClient,
      messages: formData.messages.map((message, index) => ({
        ...message,
        key: undefined,
        toolCalls: toolCallsJSON[index]
          ? (JSON.parse(toolCallsJSON[index] || '[]') as MessageToEval['toolCalls'])
          : null,
      })),
    };
    try {
      if (isEditMode && conversationEval?.id) {
        await updateConversationEval({
          conversationEvalId: conversationEval.id,
          ...payload,
        });
      } else {
        await createConversationEval(payload);
      }
      setFormData({
        businessUnitIds: [],
        mockClient: {},
        messages: [],
      });
      onClose();
    } catch (error) {
      console.error('Failed to save conversation eval:', error);
    }
  };

  useEffect(() => {
    if (messageId) {
      getEvalFromMessage({ messageId })
        .then((data) => {
          if ('data' in data && data.data) {
            setEvalFromMessage(data.data);
          }
        })
        .catch((error) => {
          console.error('Failed to get eval from message:', error);
        });
    }
  }, [messageId]);

  useEffect(() => {
    try {
      const value = JSON.parse(mockClientJSON) as typeof formData.mockClient;
      setFormData({
        ...formData,
        mockClient: value,
      });
      setErrors({
        ...errors,
        mockClient: undefined,
      });
    } catch (_error) {
      setErrors({
        ...errors,
        mockClient: 'Invalid JSON format',
      });
    }
  }, [mockClientJSON]);

  useEffect(() => {
    if (evalFromMessage) {
      setFormData({
        ...evalFromMessage,
        messages: evalFromMessage.messages.map((m) => ({ ...m, key: getRandomKey() })),
      });
      setMockClientJSON(JSON.stringify(evalFromMessage.mockClient, null, 2));
      setToolCallsJSON(evalFromMessage.messages.map((m) => (m.toolCalls ? JSON.stringify(m.toolCalls, null, 2) : '')));
    }
  }, [evalFromMessage]);

  useEffect(() => {
    const newErrors: typeof errors = {
      toolCalls: toolCallsJSON.map(() => null),
    };
    toolCallsJSON.forEach((toolCall, index) => {
      if (toolCall) {
        try {
          const value = JSON.parse(toolCall) as MessageToEval['toolCalls'];
          setFormData({
            ...formData,
            messages: formData.messages.map((message, i) => (i === index ? { ...message, toolCalls: value } : message)),
          });
        } catch (_error) {
          newErrors.toolCalls[index] = 'Invalid JSON format for tool calls';
        }
      }
    });
    setErrors(newErrors);
  }, [toolCallsJSON]);

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid size={12}>
          <FormControl fullWidth error={!!errors.businessUnitIds}>
            <InputLabel id="business-units-label">Business Units</InputLabel>
            <Select
              labelId="business-units-label"
              multiple
              value={formData.businessUnitIds}
              onChange={handleBusinessUnitChange}
              label="Business Units"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    const businessUnit = businessUnits?.find((bu) => bu.id === value);
                    return businessUnit ? <Chip key={value} label={businessUnit.name} size="small" /> : null;
                  })}
                </Box>
              )}
            >
              {businessUnits?.map((businessUnit) => (
                <MenuItem key={businessUnit.id} value={businessUnit.id}>
                  {businessUnit.name}
                </MenuItem>
              ))}
            </Select>
            {errors.businessUnitIds && <FormHelperText>{errors.businessUnitIds}</FormHelperText>}
          </FormControl>
        </Grid>

        <Grid size={12}>
          <Typography variant="h6" gutterBottom>
            Mock Client
          </Typography>
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              label="Client JSON"
              multiline
              rows={5}
              value={mockClientJSON}
              onChange={(e) => setMockClientJSON(e.target.value)}
              style={{
                backgroundColor: errors.mockClient ? '#e1c0c0' : '#c9d9c9',
                fontSize: '12px',
              }}
            />
          </Box>
        </Grid>

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h6">Mensajes</Typography>
            <Button startIcon={<AddIcon />} onClick={addMessage}>
              Agregar Mensaje
            </Button>
          </Box>
          {errors.messages && (
            <FormHelperText error sx={{ mb: 2 }}>
              {errors.messages}
            </FormHelperText>
          )}
          {formData.messages.map((message, index) => (
            <Box
              key={message.key}
              sx={{
                p: 2,
                mb: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
              }}
            >
              {!message.key && (
                <Typography variant="caption" color="error" sx={{ mb: 2 }}>
                  Message key is missing
                </Typography>
              )}
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 3 }}>
                  <FormControl fullWidth>
                    <InputLabel id={`role-label-${message.text}`}>Rol</InputLabel>
                    <Select
                      labelId={`role-label-${message.text}`}
                      value={message.role}
                      onChange={(e) => handleMessageChange(index, 'role', e.target.value)}
                      label="Role"
                    >
                      <MenuItem value="user">User</MenuItem>
                      <MenuItem value="assistant">Assistant</MenuItem>
                      <MenuItem value="tool">Tool</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, sm: 9 }}>
                  <TextField
                    fullWidth
                    label="Message Text"
                    value={message.text || ''}
                    onChange={(e) => handleMessageChange(index, 'text', e.target.value)}
                    multiline
                    rows={3}
                  />
                </Grid>

                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="File URL"
                    value={message.fileUrl || ''}
                    onChange={(e) => handleMessageChange(index, 'fileUrl', e.target.value)}
                    placeholder="https://example.com/file.jpg"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="File Name"
                    value={message.fileName || ''}
                    onChange={(e) => handleMessageChange(index, 'fileName', e.target.value)}
                    placeholder="image.jpg"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Media ID"
                    value={message.mediaId || ''}
                    onChange={(e) => handleMessageChange(index, 'mediaId', e.target.value)}
                    placeholder="123456789"
                  />
                </Grid>

                <Grid size={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={uploadingFiles[index] ? <CircularProgress size={16} /> : <AttachFileIcon />}
                      size="small"
                      disabled={uploadingFiles[index]}
                    >
                      {uploadingFiles[index] ? 'Subiendo...' : 'Adjuntar Archivo'}
                      <input
                        type="file"
                        hidden
                        accept="image/*,.pdf,.doc,.docx,.txt,.rtf"
                        onChange={(e) => {
                          void handleFileUpload(index, e);
                        }}
                      />
                    </Button>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      Soporta imágenes (jpg, png, gif, webp) y documentos (pdf, doc, docx, txt, rtf)
                    </Typography>
                  </Box>
                </Grid>

                {/* Vista previa del archivo adjunto */}
                {renderFilePreview(message, index)}

                {message.role === 'assistant' && (
                  <>
                    <Grid size={{ xs: 12, sm: 9 }}>
                      <TextField
                        fullWidth
                        label="Tool Calls"
                        value={toolCallsJSON[index] || ''}
                        onChange={(e) => handleMessageChange(index, 'toolCalls', e.target.value)}
                        multiline
                        rows={4}
                        style={{
                          backgroundColor: errors.toolCalls[index] ? '#e1c0c0' : '#c9d9c9',
                          fontSize: '12px',
                        }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 9 }}>
                      <TextField
                        fullWidth
                        label="Expected Response Description"
                        value={message.expected?.responseDescription || ''}
                        onChange={(e) => handleExpectedChange(index, 'responseDescription', e.target.value)}
                        multiline
                        rows={2}
                        placeholder="Describe la respuesta esperada para esta evaluación..."
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 3 }}>
                      <TextField
                        fullWidth
                        label="Response Distance"
                        type="number"
                        value={message.expected?.responseDistance || ''}
                        onChange={(e) => handleExpectedChange(index, 'responseDistance', e.target.value)}
                      />
                    </Grid>
                  </>
                )}
                {message.role === 'tool' && (
                  <Grid size={{ xs: 12, sm: 9 }}>
                    <TextField
                      fullWidth
                      label="Tool Call ID"
                      value={message.toolCallId || ''}
                      onChange={(e) => handleMessageChange(index, 'toolCallId', e.target.value)}
                      multiline
                      rows={4}
                    />
                  </Grid>
                )}
              </Grid>
              {formData.messages.length > 1 && (
                <IconButton
                  sx={{ position: 'absolute', top: 8, right: 8 }}
                  onClick={() => removeMessage(index)}
                  color="error"
                  size="small"
                >
                  <DeleteIcon />
                </IconButton>
              )}
            </Box>
          ))}
        </Grid>

        {/* Mensaje de advertencia para expected response description */}
        {!formData.messages.some(
          (message) => message.expected?.responseDescription && message.expected.responseDescription.trim() !== ''
        ) && (
          <Grid size={12}>
            <Box
              sx={{
                p: 2,
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'warning.dark',
                }}
              >
                ⚠️ Al menos un mensaje debe tener una descripción de respuesta esperada para poder crear la evaluación.
              </Typography>
            </Box>
          </Grid>
        )}

        <Grid size={12}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 2,
            }}
          >
            <Button variant="outlined" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={!isFormValid()}
              onClick={() => {
                handleSubmit().catch((error) => {
                  console.error('Failed to save conversation eval:', error);
                });
              }}
            >
              {isEditMode ? 'Actualizar' : 'Crear'}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ConversationEvalForm;
